import React, { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useDIContext } from '@/Framework/DI/DIContext';
import { useDictionariesContext } from '@/finsight/application/Dictionaries/DictionariesContext';
import { getFilters } from '@/finsight/application/TopCompanies/helpers';
import { IFilters } from '@/finsight/application/TopCompanies/interfaces';
import CompanyRepository from '@/finsight/infrastructure/repository/CompanyRepository';
import { getValidProductsRegions } from '@/finsight/application/helpers';
import { IDictionariesState } from '@/finsight/application/Dictionaries/helpers';
import { ICompanyByIdState } from '@/finsight/application/Deal/helpers';
import { ProfileTypes } from '@/finsight/application/actions/profile/constants';
import { useUserPlanContext } from '@/finsight/application/User/UserPlan/UserPlanContext';
import { checkAccessByProducts, checkAccessByRegions } from '@/finsight/application/User/helpers';

interface IProductRegion {
  pathname: string,
  query: {
    products?: string,
    regions?: string,
    industry?: string,
    sector?: string,
    companyViewId?: string,
    leagueType?: string,
  },
}

const usePageFilters = ({ profileType, isConsumerOverview = false, company: initCompany }) => {
  const { container } = useDIContext();
  const [company, setCompany] = useState(initCompany || null);
  const [additionalFilters, setAdditionalFilters] = useState({});
  const router = useRouter();
  const { pathname, query }: IProductRegion = router;
  const { industry, sector, companyViewId, leagueType } = query;
  const {
    regionsList, productsList, sectorsList,
    industriesList, mapping,
  }: IDictionariesState = useDictionariesContext();
  const {
    products: accessibleProductsIds,
    regions: accessibleRegionsIds,
  } = useUserPlanContext();
  const { products: userProducts, regions: userRegions } = useUserPlanContext();

  const { productIds, regionIds } = getValidProductsRegions({
    pathname,
    dictionaries: { regionsList, productsList },
    query,
  });

  const checkAccessByUserRegions = () => {
    return checkAccessByRegions(regionIds, userRegions);
  };

  const checkAccessByUserProducts = () => {
    return checkAccessByProducts(productIds, userProducts);
  };

  const preparedFilters: IFilters = {
    profileType,
    isConsumerOverview,
    productIds,
    regionIds,
    industry,
    sector,
    sectorsList,
    industriesList,
    mapping,
    leagueType,
    ...additionalFilters,
  };

  if (companyViewId && company) {
    preparedFilters.includedParentId = company.id;
  }

  const pageFilters = getFilters(preparedFilters);

  const { productIds: includedProductIds, regionIds: includedRegionIds } = pageFilters;

  const productFilters = productsList.map((product) => ({
    ...product,
    isActive: includedProductIds.includes(product.id),
    tooltip: product.displayName,
  }));

  const regionFilters = regionsList.map((region) => ({
    ...region,
    isActive: includedRegionIds.includes(region.id),
    tooltip: `Includes deals issued with currencies or issuers domiciled in ${ region.name }`,
  }));

  const firstRegion = regionFilters.find(({ id }) => id === includedRegionIds[0]);
  const firstProduct = productFilters.find(({ id }) => id === includedProductIds[0]);

  const accessibleProducts = checkAccessByUserProducts() && checkAccessByUserRegions()
    ? productFilters.filter(({ id }) => accessibleProductsIds.includes(id))
    : [];
  const accessibleRegions = checkAccessByUserProducts() && checkAccessByUserRegions()
    ? regionFilters.filter(({ id }) => accessibleRegionsIds.includes(id))
    : [];

  const getParentCompany = async (): Promise<void> => {
    const companyRepository = container.get<CompanyRepository>(CompanyRepository);
    try {
      const company = await companyRepository.getCompanyByViewId({
        viewId: companyViewId,
      });
      setCompany(company);
    } catch (e) {
      // @todo throw redirect server side
      await router.push('/404');
    }
  };

  useEffect(() => {
    if (initCompany?.hasServerError) {
      getParentCompany();
    }
  }, [initCompany]);

  useEffect(() => {
    setCompany(initCompany);
  }, [initCompany]);

  return {
    company,
    productFilters,
    regionFilters,
    includedProductIds,
    includedRegionIds,
    firstRegion,
    firstProduct,
    pageFilters,
    accessibleProducts,
    accessibleRegions,
    setAdditionalFilters,
    checkAccessByUserRegions,
    checkAccessByUserProducts,
  };
};

type PageFiltersContextType = ReturnType<typeof usePageFilters>;

export const PageFiltersContext = createContext<PageFiltersContextType>(null);

export const usePageFiltersContext = () => {
  const context = useContext(PageFiltersContext);
  if (!context) {
    throw new Error('usePageFilters must be used within the PageFiltersContext');
  }
  return context;
};

interface IProps {
  profileType?: ProfileTypes,
  isConsumerOverview?: boolean,
  company?: ICompanyByIdState,
  children: React.ReactNode,
}

const PageFiltersContextProvider = ({ children, profileType, isConsumerOverview, company }: IProps) => (
  <PageFiltersContext.Provider value={ usePageFilters({ profileType, isConsumerOverview, company }) }>
    { children }
  </PageFiltersContext.Provider>
);

export default PageFiltersContextProvider;
